import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DynamicSidebar from '../../../components/ui/DynamicSidebar';
import DashboardHeader from '../../../components/ui/DashboardHeader';
import Icon from '../../../components/AppIcon';
import { format } from 'date-fns';

const STATUS_MAP = {
  0: { label: 'Pending', color: 'bg-warning/10 text-warning', icon: 'Clock' },
  1: { label: 'Success', color: 'bg-success/10 text-success', icon: 'CheckCircle' },
  2: { label: 'Processing', color: 'bg-primary/10 text-primary', icon: 'Loader2' },
  3: { label: 'Failed', color: 'bg-error/10 text-error', icon: 'XCircle' },
  4: { label: 'Reversed', color: 'bg-muted text-text-secondary', icon: 'RotateCcw' },
};

const TIMELINE_STEPS = [
  { status: 0, label: 'Initiated', description: 'Transaction was created and submitted' },
  { status: 2, label: 'Processing', description: 'Transaction is being processed by the payment gateway' },
  { status: 1, label: 'Completed', description: 'Transaction completed successfully' },
];

const InfoRow = ({ label, value, mono = false, highlight = false }) => (
  <div className="flex flex-col sm:flex-row sm:items-start py-3 border-b border-border last:border-0">
    <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide sm:w-48 shrink-0 mb-1 sm:mb-0">{label}</span>
    <span className={`text-sm break-all ${mono ? 'font-mono' : ''} ${highlight ? 'font-semibold text-text-primary' : 'text-text-primary'}`}>
      {value ?? '-'}
    </span>
  </div>
);

const SectionCard = ({ title, icon, children }) => (
  <div className="bg-surface border border-border rounded-xl overflow-hidden mb-5">
    <div className="flex items-center space-x-2 px-5 py-4 border-b border-border bg-muted/30">
      <Icon name={icon} size={18} className="text-primary" />
      <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
    </div>
    <div className="px-5 py-2">{children}</div>
  </div>
);

const TransactionDetailPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const tx = location?.state?.transaction || {};

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return format(new Date(dateStr), 'MMM dd, yyyy HH:mm:ss'); } catch { return dateStr; }
  };

  const statusInfo = STATUS_MAP?.[tx?.status];
  const paymentType = location?.state?.paymentType || 'deposit';

  const getTimelineSteps = () => {
    const currentStatus = tx?.status;
    if (currentStatus === 3) {
      return [
        { status: 0, label: 'Initiated', description: 'Transaction was created and submitted', done: true },
        { status: 3, label: 'Failed', description: tx?.status_response || 'Transaction failed', done: true, failed: true },
      ];
    }
    if (currentStatus === 4) {
      return [
        { status: 0, label: 'Initiated', description: 'Transaction was created and submitted', done: true },
        { status: 1, label: 'Completed', description: 'Transaction completed successfully', done: true },
        { status: 4, label: 'Reversed', description: 'Transaction was reversed', done: true, reversed: true },
      ];
    }
    return TIMELINE_STEPS?.map(step => ({
      ...step,
      done: step?.status <= (currentStatus === 1 ? 2 : currentStatus),
      active: step?.status === (currentStatus === 1 ? 2 : currentStatus),
    }));
  };

  const timelineSteps = getTimelineSteps();

  const handleDownloadReceipt = () => {
    const content = [
      '=== TRANSACTION RECEIPT ===',
      '',
      `Date: ${formatDate(tx?.created_at)}`,
      `Client Reference: ${tx?.client_reference || '-'}`,
      `Gateway Reference: ${tx?.gateway_reference || '-'}`,
      `Amount: ${tx?.currency} ${tx?.amount != null ? Number(tx?.amount)?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}`,
      `Status: ${statusInfo?.label || tx?.status}`,
      `Merchant: ${tx?.merchant || '-'}`,
      '',
      '--- Customer ---',
      `Name: ${tx?.first_name || ''} ${tx?.last_name || ''}`,
      `Email: ${tx?.email || '-'}`,
      `Mobile: ${tx?.mobile_number || '-'}`,
      '',
      '--- Service ---',
      `Service Code: ${tx?.service?.service_code || '-'}`,
      `Service Name: ${tx?.service?.service_name || '-'}`,
      `Service Type: ${tx?.service?.service_type || '-'}`,
      `Transaction Type: ${tx?.service?.transaction_type || '-'}`,
      '',
      '=========================',
    ]?.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt-${tx?.client_reference || 'transaction'}.txt`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <DynamicSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        <div className="p-6 max-w-5xl">
          {/* Back Button & Header */}
          <div className="flex items-center space-x-3 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-smooth"
            >
              <Icon name="ArrowLeft" size={16} className="text-text-secondary" />
              <span>Back</span>
            </button>
            <div>
              <h1 className="text-xl font-semibold text-text-primary">Transaction Details</h1>
              <p className="text-text-secondary text-xs mt-0.5">
                {paymentType === 'deposit' ? 'Deposit' : 'Payout'} · {tx?.client_reference || 'N/A'}
              </p>
            </div>
          </div>

          {/* Status Banner */}
          {statusInfo && (
            <div className={`flex items-center space-x-3 p-4 rounded-xl border mb-5 ${statusInfo?.color} border-current/20`}>
              <Icon name={statusInfo?.icon} size={20} className={tx?.status === 2 ? 'animate-spin' : ''} />
              <div>
                <p className="font-semibold text-sm">{statusInfo?.label}</p>
                <p className="text-xs opacity-80">{tx?.status_response || 'No status description available'}</p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-xl font-bold">
                  {tx?.currency} {tx?.amount != null ? Number(tx?.amount)?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                </p>
                <p className="text-xs opacity-70">{formatDate(tx?.created_at)}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-5">
              {/* Transaction Information */}
              <SectionCard title="Transaction Information" icon="CreditCard">
                <InfoRow label="Amount" value={`${tx?.currency || ''} ${tx?.amount != null ? Number(tx?.amount)?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}`} highlight />
                <InfoRow label="Currency" value={tx?.currency} />
                <InfoRow label="Status" value={statusInfo?.label || tx?.status} />
                <InfoRow label="Business" value={tx?.business} mono />
                <InfoRow label="Client Reference" value={tx?.client_reference} mono />
                <InfoRow label="Gateway Reference" value={tx?.gateway_reference} mono />
                <InfoRow label="Merchant" value={tx?.merchant} />
                <InfoRow label="Callback URL" value={tx?.callback_url} mono />
                <InfoRow label="Created At" value={formatDate(tx?.created_at)} />
                <InfoRow label="Updated At" value={formatDate(tx?.updated_at)} />
                <InfoRow label="Status Response" value={tx?.status_response} />
              </SectionCard>

              {/* Customer Details */}
              <SectionCard title="Customer Details" icon="User">
                <InfoRow label="First Name" value={tx?.first_name} />
                <InfoRow label="Last Name" value={tx?.last_name} />
                <InfoRow label="Email" value={tx?.email} />
                <InfoRow label="Mobile Number" value={tx?.mobile_number} mono />
              </SectionCard>

              {/* Service Details */}
              <SectionCard title="Service Details" icon="Settings">
                <InfoRow label="Service Code" value={tx?.service?.service_code} mono />
                <InfoRow label="Service Name" value={tx?.service?.service_name} />
                <InfoRow label="Service Type" value={tx?.service?.service_type} />
                <InfoRow label="Transaction Type" value={tx?.service?.transaction_type} />
              </SectionCard>
            </div>

            {/* Right Column */}
            <div className="space-y-5">
              {/* Action Buttons */}
              <div className="bg-surface border border-border rounded-xl overflow-hidden">
                <div className="flex items-center space-x-2 px-5 py-4 border-b border-border bg-muted/30">
                  <Icon name="Zap" size={18} className="text-primary" />
                  <h2 className="text-sm font-semibold text-text-primary">Actions</h2>
                </div>
                <div className="p-4 space-y-3">
                  <button
                    onClick={handleDownloadReceipt}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-smooth text-sm font-medium"
                  >
                    <Icon name="Download" size={16} />
                    <span>Download Receipt</span>
                  </button>
                  <button
                    onClick={() => alert('Refund request submitted. Our team will review it shortly.')}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg border border-border text-text-primary hover:bg-muted transition-smooth text-sm font-medium"
                  >
                    <Icon name="RotateCcw" size={16} className="text-warning" />
                    <span>Request Refund</span>
                  </button>
                  <button
                    onClick={() => alert('Dispute raised. Reference: ' + (tx?.client_reference || 'N/A'))}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg border border-border text-text-primary hover:bg-muted transition-smooth text-sm font-medium"
                  >
                    <Icon name="AlertTriangle" size={16} className="text-error" />
                    <span>Raise Dispute</span>
                  </button>
                  <button
                    onClick={() => alert('Support ticket created for transaction: ' + (tx?.client_reference || 'N/A'))}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg border border-border text-text-primary hover:bg-muted transition-smooth text-sm font-medium"
                  >
                    <Icon name="Headphones" size={16} className="text-primary" />
                    <span>Contact Support</span>
                  </button>
                </div>
              </div>

              {/* Transaction Timeline */}
              <div className="bg-surface border border-border rounded-xl overflow-hidden">
                <div className="flex items-center space-x-2 px-5 py-4 border-b border-border bg-muted/30">
                  <Icon name="GitBranch" size={18} className="text-primary" />
                  <h2 className="text-sm font-semibold text-text-primary">Timeline</h2>
                </div>
                <div className="p-5">
                  <div className="relative">
                    {timelineSteps?.map((step, idx) => (
                      <div key={idx} className="flex items-start space-x-3 mb-4 last:mb-0">
                        <div className="relative flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                            step?.failed ? 'bg-error/10 text-error' : step?.reversed ?'bg-muted text-text-secondary' :
                            step?.done ? 'bg-success/10 text-success': 'bg-muted text-text-secondary'
                          }`}>
                            <Icon
                              name={step?.failed ? 'XCircle' : step?.reversed ? 'RotateCcw' : step?.done ? 'CheckCircle' : 'Circle'}
                              size={16}
                            />
                          </div>
                          {idx < timelineSteps?.length - 1 && (
                            <div className={`w-0.5 h-8 mt-1 ${
                              timelineSteps?.[idx + 1]?.done ? 'bg-success/40' : 'bg-border'
                            }`} />
                          )}
                        </div>
                        <div className="pt-1">
                          <p className={`text-sm font-medium ${
                            step?.failed ? 'text-error' : step?.reversed ?'text-text-secondary' :
                            step?.done ? 'text-text-primary': 'text-text-secondary'
                          }`}>{step?.label}</p>
                          <p className="text-xs text-text-secondary mt-0.5">{step?.description}</p>
                          {step?.done && idx === 0 && (
                            <p className="text-xs text-text-secondary mt-0.5">{formatDate(tx?.created_at)}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Receipts */}
              <div className="bg-surface border border-border rounded-xl overflow-hidden">
                <div className="flex items-center space-x-2 px-5 py-4 border-b border-border bg-muted/30">
                  <Icon name="FileText" size={18} className="text-primary" />
                  <h2 className="text-sm font-semibold text-text-primary">Receipts</h2>
                </div>
                <div className="p-5">
                  <div className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-smooth" onClick={handleDownloadReceipt}>
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon name="FileText" size={16} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate">Transaction Receipt</p>
                      <p className="text-xs text-text-secondary">{formatDate(tx?.created_at)}</p>
                    </div>
                    <Icon name="Download" size={14} className="text-text-secondary shrink-0" />
                  </div>
                  {tx?.status !== 1 && (
                    <p className="text-xs text-text-secondary mt-3 text-center">
                      Official receipt available after transaction completes
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TransactionDetailPage;
